import type { Router } from 'vue-router';
import { captureException } from '@core/plugins/sentry';
import { isLikelyBot } from '@core/services/bot-detection';

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /Loading chunk [\w-]+ failed/,
  /ChunkLoadError/,
  /Loading CSS chunk [\w-]+ failed/,
  /Importing a module script failed/, // Safari-specific error message
];

// Reload attempt tracking constants
const STORAGE_KEY = 'chunk_reload_attempts';
const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_WINDOW_MS = 60 * 1000; // 1 minute window to track attempts

// Track if handler is already set up to prevent duplicate listeners
let isSetup = false;

interface ReloadAttemptData {
  count: number;
  firstAttemptTime: number;
}

/**
 * Get the current reload attempt data from sessionStorage
 */
function getReloadAttempts(): ReloadAttemptData {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
  } catch {
    // sessionStorage might be unavailable (private browsing, etc.)
  }
  return { count: 0, firstAttemptTime: 0 };
}

/**
 * Increment the reload attempt counter
 */
function incrementReloadAttempts(): number {
  const now = Date.now();
  let attempts = getReloadAttempts();

  // Reset counter if outside the time window
  if (now - attempts.firstAttemptTime > RELOAD_WINDOW_MS) {
    attempts = { count: 0, firstAttemptTime: now };
  }

  // Set first attempt time if this is the first attempt
  if (attempts.count === 0) {
    attempts.firstAttemptTime = now;
  }

  attempts.count += 1;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
  } catch {
    // sessionStorage might be unavailable
  }

  return attempts.count;
}

/**
 * Clear the reload attempt counter (called on successful page load)
 */
export function clearReloadAttempts(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage might be unavailable
  }
}

/**
 * Check if we should reload the page based on attempt count
 * Also considers time window - if outside the window, counter is effectively reset
 */
function shouldReload(): boolean {
  const now = Date.now();
  const attempts = getReloadAttempts();

  // If outside the time window, counter is effectively reset
  if (now - attempts.firstAttemptTime > RELOAD_WINDOW_MS) {
    return true;
  }

  return attempts.count < MAX_RELOAD_ATTEMPTS;
}

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));
  }
  if (typeof error === 'string') {
    return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(error));
  }
  return false;
}

function reloadPage(): void {
  window.location.reload();
}

export type CleanupFunction = () => void;

export function setupChunkLoadErrorHandler(router: Router): CleanupFunction {
  // Prevent duplicate registration (e.g., during HMR)
  if (isSetup) {
    return () => {};
  }
  isSetup = true;

  // Clear reload attempts on successful page load
  // This runs after the page has successfully loaded, meaning chunks loaded fine
  clearReloadAttempts();

  // Cache bot detection result (user agent doesn't change during session)
  const isBotUser = isLikelyBot();

  /**
   * Attempt to reload the page if conditions are met:
   * - Not a bot
   * - Haven't exceeded max reload attempts
   *
   * Returns the reload attempt number (0 if skipped or max reached).
   * The caller is responsible for capturing the original error to Sentry
   * with the attempt number as a tag — we intentionally avoid sending a
   * separate Sentry event here to reduce quota usage.
   */
  const attemptReload = (tags: Record<string, string | undefined>): number => {
    if (isBotUser) {
      return 0;
    }

    if (!shouldReload()) {
      // Max attempts reached, log to Sentry and don't reload
      captureException(new Error('ChunkLoadError: Max reload attempts reached'), {
        tags: {
          ...tags,
          maxAttemptsReached: 'true',
        },
      });
      return 0;
    }

    const attemptNumber = incrementReloadAttempts();

    setTimeout(() => {
      reloadPage();
    }, 100);

    return attemptNumber;
  };

  // Handle navigation failures due to chunk load errors
  router.onError((error: Error, to, from) => {
    if (isChunkLoadError(error)) {
      const tags: Record<string, string | undefined> = {
        errorType: 'ChunkLoadError',
        source: 'router.onError',
        targetRoute: to?.fullPath,
        sourceRoute: from?.fullPath,
        isBot: String(isBotUser),
      };

      const attempt = attemptReload(tags);
      if (attempt > 0) {
        tags.reloadAttempt = String(attempt);
      }

      captureException(error, { tags });
    }
  });

  // Handle global unhandled errors for chunks that fail outside of navigation
  const handleWindowError = (event: ErrorEvent) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      event.preventDefault();

      const tags: Record<string, string | undefined> = {
        errorType: 'ChunkLoadError',
        source: 'window.error',
        isBot: String(isBotUser),
      };

      const attempt = attemptReload(tags);
      if (attempt > 0) {
        tags.reloadAttempt = String(attempt);
      }

      captureException(event.error || new Error(event.message), { tags });
    }
  };

  // Handle unhandled promise rejections (for async import failures)
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();

      const tags: Record<string, string | undefined> = {
        errorType: 'ChunkLoadError',
        source: 'unhandledrejection',
        isBot: String(isBotUser),
      };

      const attempt = attemptReload(tags);
      if (attempt > 0) {
        tags.reloadAttempt = String(attempt);
      }

      captureException(event.reason, { tags });
    }
  };

  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Return cleanup function for testing and HMR
  return () => {
    window.removeEventListener('error', handleWindowError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    isSetup = false;
  };
}

// Reset function for testing purposes
export function resetChunkLoadErrorHandler(): void {
  isSetup = false;
}
